import {useEffect, startTransition, useState, useMemo, useRef} from 'react';
import {
	useLocation,
	useServerData,
	useSSRResponse,
	useSSRRedirect,
	useSearchMgrInstance,
} from 'coreHooks';
import {useCategoryActions} from 'coreHooks/categories';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {parseRefinements, initSearchManager, collectStaticProductIds, processOffsetTiles} from './helpers';
import {
	CategoryPageQuery,
	UsePLPDataParams,
	UseHandlePLPDataParams,
	UseHandlePLPDataResult,
	UsePreparePLPDataResult,
	UsePLPDataResult,
} from './ICategory';

import {FIRST_PAGE, MAX_CACHE_AGE, QUERY_MIN_LENGTH} from 'brandConstants';
import {addCategoryPageCacheTags, ICategoryResult} from 'coreUtils/categories';
import SearchModel from 'coreClasses/search/SearchModel';

export const usePreparePLPData = (routeInfo: UsePLPDataParams['routeInfo']): UsePreparePLPDataResult => {
	const location = useLocation();
	const query: CategoryPageQuery = useMemo(() => {
		return {
			...(routeInfo?.queryParams || {}),
			...Object.fromEntries(new URLSearchParams(location.search).entries()),
		};
	}, [routeInfo, location]);
	const queryPage = useMemo(() => parseInt(query.page as string, 10), [query.page]);
	const pageToLoad = useMemo(() => queryPage || FIRST_PAGE, [queryPage]);
	const {cgid, q: searchQuery, srule} = query;
	const refinements = useMemo(() => parseRefinements(query), [query]);
	const isQueryTooShort = useMemo(
		() => !cgid && (!searchQuery || searchQuery.length < QUERY_MIN_LENGTH),
		[cgid, searchQuery],
	);

	return {
		cgid,
		searchQuery,
		pageToLoad,
		refinements,
		isQueryTooShort,
		srule,
		query,
		queryPage
	};
};

export const useHandlePLPData = ({
	routeInfo,
	searchInstanceKey,
	searchType,
	cgid,
	searchQuery,
	refinements,
	srule,
	pageToLoad,
	isQueryTooShort,
	localizedContent,
}: UseHandlePLPDataParams): UseHandlePLPDataResult | undefined => {
	const location = useLocation();
	const {managerRepository} = useGeneralProvider();
	const searchMgr = useSearchMgrInstance({
		key: searchInstanceKey,
		searchType,
	});
	const [reloadFlag, setReloadFlag] = useState(false);
	const categoryActions = useCategoryActions();
	const res = useSSRResponse();
	const redirect = useSSRRedirect();

	const lastUrlRef = useRef<string | null>(null);

	useEffect(() => {
		const url = `${location.pathname}${location.search}`;
		// A new mount or URL, not the PLP shown again by the keep-alive (same URL): its data is on screen.
		const isNewUrl = lastUrlRef.current !== url;

		lastUrlRef.current = url;

		if (isNewUrl && location.isPop && !location.justLanded) {
			startTransition(() => {
				setReloadFlag((prev) => !prev);
			});
		}
	}, [location.isPop, location.justLanded, location.search, location.pathname]);

	const data = useServerData(
		'category-page-data',
		async () => {
			if (!cgid && !searchQuery) {
				return {
					notFound: false,
					redirected: false,
				};
			}

			// Yield before Redux dispatches so they run outside the synchronous
			// render phase of useSuspenseQuery, avoiding "Cannot update component
			// X while rendering component Y" warnings (e.g. RefinementsPopup).
			await Promise.resolve();

			initSearchManager(searchMgr, cgid, searchQuery, srule, refinements, pageToLoad);

			const searchPromise: Promise<SearchModel | undefined> = !isQueryTooShort
				? searchMgr.search()
				: Promise.resolve(undefined);

			const categoryPromise: Promise<ICategoryResult | undefined> = cgid
				? categoryActions.getCategoryPromise(cgid)
				: Promise.resolve(undefined);

			const staticProductIds = collectStaticProductIds(localizedContent?.customElementIncludeCollection?.items);

			const productsPromise: Promise<unknown> =
				staticProductIds.size > 0
					? managerRepository.getProductsMgr().getProductsSync(Array.from(staticProductIds))
					: Promise.resolve(undefined);

			const [searchResult, categoryModel] = await Promise.all([
				searchPromise,
				categoryPromise,
				productsPromise,
			]);

			if (searchResult && pageToLoad > searchResult.getLastPageNumber() && searchResult.getLastPageNumber() > 0) {
				const params = new URLSearchParams(location.search || '');
				params.delete('page');

				const redirectUrl = `${location.pathname}${params.toString() ? '?' + params.toString() : ''}`;

				redirect(302, redirectUrl, undefined);

				return {redirected: true, notFound: false};
			}

			if (searchResult) {
				processOffsetTiles(
					localizedContent?.customElementIncludeCollection?.items,
					localizedContent?.productsGridWidgetsCollection?.items,
					searchResult.getPageSize(),
					pageToLoad,
				);
			}

			if (
				categoryModel &&
				categoryModel.hasOwnProperty('getType') &&
				categoryModel?.getType()?.endsWith('category-not-found')
			) {
				return {notFound: true, redirected: false};
			}

			if (searchResult && searchResult.getSearchRedirectUrl()) {
				redirect(301, searchResult.getSearchRedirectUrl(), undefined);

				return {redirected: true, notFound: false};
			}

			if (res) {
				res.setCache?.(routeInfo?.expirationTime || MAX_CACHE_AGE);
			}

			addCategoryPageCacheTags(res, categoryModel, searchResult, localizedContent, staticProductIds);

			return {redirected: false, notFound: false};
		},
		[reloadFlag, routeInfo, searchQuery],
	);

	return data;
};

export default (props: UsePLPDataParams): UsePLPDataResult => {
	const {routeInfo, searchInstanceKey, searchType, localizedContent} = props;
	const preparedData = usePreparePLPData(props.routeInfo);
	const data = useHandlePLPData({
		routeInfo,
		searchInstanceKey,
		searchType,
		localizedContent,
		...preparedData,
	});

	return {
		...preparedData,
		data,
	};
};
